import { fetchWithToken } from '@utils/api';
import { API_URL } from '@data/constants';

import type { User } from '@services/users';
import type { Event } from '@services/events';
import type { EventArea } from '@services/eventsAreas';

export enum RegistrationStatus {
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  INTERVIEWED = 'INTERVIEWED',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
}

export interface Registration {
  id: null | number;
  user: User;
  event: Event;
  preferredEventAreas: EventArea[];
  assignedEventAreas: EventArea[];
  status: RegistrationStatus;
}

export async function getRegistrations({
  ended,
  status,
  limit,
  offset,
  search,
  ordering,
  provinces,
  eventId,
  userId,
}: {
  ended?: boolean;
  status?: string[] | RegistrationStatus;
  limit?: number;
  offset?: number;
  search?: string;
  ordering?: string;
  provinces?: string[];
  eventId?: number;
  userId?: number;
} = {}): Promise<{ count: number; results: Registration[] }> {
  const params = new URLSearchParams();

  if (ended !== undefined) {
    params.append('ended', String(ended));
  }

  if (typeof status === 'string') {
    params.append('status', status);
  }

  if (Array.isArray(status)) {
    params.append('status', status.join(','));
  }

  if (limit) {
    params.append('limit', String(limit));
  }

  if (offset) {
    params.append('offset', String(offset));
  }

  if (search) {
    params.append('search', search);
  }

  if (ordering) {
    params.append('ordering', ordering);
  }

  if (provinces) {
    params.append('province', provinces.join(','));
  }

  if (eventId) {
    params.append('event_id', String(eventId));
  }

  if (userId) {
    params.append('user_id', String(userId));
  }

  const response = await fetchWithToken(
    `${API_URL}/api/registrations?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error('Error getting registrations');
  }

  const { data } = (await response.json()) as {
    data: { count: number; results: Registration[] };
  };

  return data;
}

export async function createRegistration({
  event,
  preferredEventAreas,
}: {
  event: number;
  preferredEventAreas: number[];
}): Promise<Registration> {
  const response = await fetchWithToken(`${API_URL}/api/registrations`, {
    method: 'POST',
    body: JSON.stringify({
      event,
      preferredEventAreas,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Error creating registration');
  }

  const { data } = (await response.json()) as { data: Registration };

  return data;
}

export async function updateRegistration({
  registrationId,
  status,
  preferredEventAreas,
  assignedEventAreas,
}: {
  registrationId: number;
  status?: RegistrationStatus;
  preferredEventAreas?: number[];
  assignedEventAreas?: number[];
}): Promise<Registration> {
  const response = await fetchWithToken(
    `${API_URL}/api/registrations/${registrationId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        preferredEventAreas,
        assignedEventAreas,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Error updating registration');
  }

  const { data } = (await response.json()) as { data: Registration };

  return data;
}

export async function getRegistrationsExport({
  ended,
  status,
  limit,
  offset,
  search,
  provinces,
  eventId,
  userId,
}: {
  ended?: boolean;
  status?: string[] | RegistrationStatus;
  limit?: number;
  offset?: number;
  search?: string;
  provinces?: string[];
  eventId?: number;
  userId?: number;
} = {}) {
  const params = new URLSearchParams();

  if (ended !== undefined) {
    params.append('ended', String(ended));
  }

  if (typeof status === 'string') {
    params.append('status', status);
  }

  if (Array.isArray(status)) {
    params.append('status', status.join(','));
  }

  if (limit) {
    params.append('limit', String(limit));
  }

  if (offset) {
    params.append('offset', String(offset));
  }

  if (search) {
    params.append('search', search);
  }

  if (provinces) {
    params.append('province', provinces.join(','));
  }

  if (eventId) {
    params.append('event_id', String(eventId));
  }

  if (userId) {
    params.append('user_id', String(userId));
  }

  const response = await fetchWithToken(
    `${API_URL}/api/registrations/export?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error('Error getting registrations export');
  }

  const blob = await response.blob();

  console.log(blob);

  return blob;
}
